import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  getAllRestaurantsAnalytics,
  getDashboardOverview,
  getSalesTrend,
  getTopItems,
  getPeakHours,
  getCategoryPerformance,
  getOrderStatusBreakdown,
  getRestaurant,
} from '../../services/api';
import { formatInteger } from '../../utils/formatNumber';
import PriceTag from '../../components/ui/PriceTag';
import AppShell from '../../components/layout/AppShell';
import './AdminAnalyticsDashboard.css';

/* ─── Constants ─────────────────────────────────────────────────────────────── */
// Mesoon chart palette — colorblind-aware: green · amber · blue · yellow · slate · violet · magenta · lime
const COLORS = ['#10B981','#F59E0B','#3B82F6','#FBBF24','#9CA3AF','#7C3AED','#DB2777','#65A30D'];
const getPeriods = (t) => [
  { key: 'day',   label: t('analytics.today')   },
  { key: 'week',  label: t('analytics.week')    },
  { key: 'month', label: t('analytics.month')   },
];

/* ─── Small helpers ─────────────────────────────────────────────────────────── */
const fmtMoney = (val, symbol = '$', position = 'before') => {
  const n = parseFloat(val || 0).toFixed(2);
  return position === 'after' ? `${n}${symbol}` : `${symbol}${n}`;
};

const growthClass  = (g) => (g >= 0 ? 'aad-positive' : 'aad-negative');
const growthArrow  = (g) => (g >= 0 ? '↑' : '↓');

/* ─── KPI Card ──────────────────────────────────────────────────────────────── */
const KpiCard = ({ icon, label, value, sub, growth, growthSuffix, accentColor }) => (
  <div className="aad-kpi" style={{ borderTopColor: accentColor }}>
    <div className="aad-kpi-top">
      <span className="aad-kpi-icon">{icon}</span>
      <span className="aad-kpi-label">{label}</span>
    </div>
    <div className="aad-kpi-value">{value}</div>
    {growth != null && growthSuffix && (
      <div className={`aad-kpi-growth ${growthClass(growth)}`}>
        {growthArrow(growth)} {Math.abs(growth)}% {growthSuffix}
      </div>
    )}
    {sub && <div className="aad-kpi-sub">{sub}</div>}
  </div>
);

/* ─── Skeleton ──────────────────────────────────────────────────────────────── */
const Skel = ({ h = 180 }) => <div className="aad-skel" style={{ height: h }} />;

/* ════════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════════════════════ */
const AdminAnalyticsDashboard = () => {
  const { t } = useTranslation();

  /* ── UI state ─────────────────────────────────────────────────────────────── */
  // 'platform' = all-restaurants overview
  // 'single'   = one restaurant drilled into
  const [view,    setView]    = useState('platform');
  const [period,  setPeriod]  = useState('week');

  const [drillId,   setDrillId]   = useState(null);   // restaurant_id being drilled
  const [drillName, setDrillName] = useState('');

  /* ── Platform data ────────────────────────────────────────────────────────── */
  const [platformLoading,  setPlatformLoading]  = useState(true);
  const [platformOverview, setPlatformOverview] = useState(null);
  const [perRestaurant,    setPerRestaurant]    = useState([]);
  const [platformTrend,    setPlatformTrend]    = useState([]);

  /* ── Single-restaurant data ───────────────────────────────────────────────── */
  const [singleLoading,    setSingleLoading]    = useState(false);
  const [singleOverview,   setSingleOverview]   = useState(null);
  const [singleTrend,      setSingleTrend]      = useState([]);
  const [singleTopItems,   setSingleTopItems]   = useState([]);
  const [singlePeakHours,  setSinglePeakHours]  = useState([]);
  const [singleCategories, setSingleCategories] = useState([]);
  const [singleStatus,     setSingleStatus]     = useState(null);
  const [singleCurrency,   setSingleCurrency]   = useState({ code: 'USD', symbol: '$', position: 'before' });

  /* ── Fetch platform ───────────────────────────────────────────────────────── */
  const loadPlatform = useCallback(async () => {
    setPlatformLoading(true);
    try {
      const data = await getAllRestaurantsAnalytics(period);
      setPlatformOverview(data.platformOverview);
      setPerRestaurant(data.perRestaurant);
      setPlatformTrend(data.salesTrend);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Platform analytics error:', e);
    } finally {
      setPlatformLoading(false);
    }
  }, [period]);

  useEffect(() => { loadPlatform(); }, [loadPlatform]);

  /* ── Fetch single restaurant ──────────────────────────────────────────────── */
  const loadSingle = useCallback(async (id) => {
    setSingleLoading(true);
    try {
      const [ov, trend, items, hours, cats, status, rest] = await Promise.all([
        getDashboardOverview(id, period),
        getSalesTrend(id, period),
        getTopItems(id, period),
        getPeakHours(id, period),
        getCategoryPerformance(id, period),
        getOrderStatusBreakdown(id, period),
        getRestaurant(id),
      ]);
      setSingleOverview(ov.overview);
      setSingleTrend(trend.salesData      || []);
      setSingleTopItems(items.topItems    || []);
      setSinglePeakHours(hours.peakHours  || []);
      setSingleCategories(cats.categories || []);
      setSingleStatus(status);
      setSingleCurrency(rest?.data?.currency || { code: 'USD', symbol: '$', position: 'before' });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Single restaurant analytics error:', e);
    } finally {
      setSingleLoading(false);
    }
  }, [period]);

  // Reload single data when period changes while in drill-down
  useEffect(() => {
    if (view === 'single' && drillId) loadSingle(drillId);
  }, [view, drillId, loadSingle]);

  /* ── Drill down ───────────────────────────────────────────────────────────── */
  const drillInto = (row) => {
    setDrillId(row.restaurant_id);
    setDrillName(row.restaurant_name);
    setView('single');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadSingle(row.restaurant_id);
  };

  const backToPlatform = () => {
    setView('platform');
    setDrillId(null);
  };

  /* ── Convenience formatters for single-restaurant view ───────────────────── */
  // String version — used by recharts tooltips/axes (only accept strings).
  const $s = (v) => fmtMoney(v, singleCurrency.symbol, singleCurrency.position);
  // JSX version — used by KPI cards, table cells, and status tiles so OMR
  // displays the official Omani Rial sign instead of a text symbol.
  const SinglePrice = ({ amount, size = 'md' }) => (
    <PriceTag amount={amount} currency={singleCurrency} size={size} color="inherit" weight="bold" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
  );

  /* ══════════════════════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════════════════════ */
  return (
    <AppShell>
    <div className="aad-page">

      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <header className="aad-header">
        <div className="aad-header-left">
          {view === 'single' && (
            <button className="aad-back" onClick={backToPlatform}>
              {t('analytics.admin.backToAll')}
            </button>
          )}
          <div className="aad-header-icon">📊</div>
          <div>
            <h1 className="aad-title">
              {view === 'platform' ? t('analytics.admin.platformTitle') : drillName}
            </h1>
            <p className="aad-subtitle">
              {view === 'platform'
                ? t('analytics.admin.platformSubtitle')
                : t('analytics.admin.singleSubtitle')}
            </p>
          </div>
        </div>

        <div className="aad-header-right">
          {view === 'single' && drillId && (
            <Link
              to={`/manager/restaurant/${drillId}/analytics`}
              className="aad-open-manager"
              target="_blank"
              rel="noreferrer"
            >
              {t('analytics.admin.openManagerView')}
            </Link>
          )}

          <div className="aad-period-bar">
            {getPeriods(t).map((p) => (
              <button
                key={p.key}
                className={`aad-period-btn${period === p.key ? ' active' : ''}`}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ════════════════════════════════════════════════════════════════════════
          PLATFORM VIEW
          ════════════════════════════════════════════════════════════════════════ */}
      {view === 'platform' && (
        <>
          {/* KPI grid */}
          <div className="aad-kpi-grid">
            {platformLoading
              ? [...Array(5)].map((_, i) => <Skel key={i} h={116} />)
              : platformOverview && (
                <>
                  <KpiCard
                    icon="💰" label={t('analytics.totalRevenue')} accentColor="#ea580c"
                    value={fmtMoney(platformOverview.totalRevenue)}
                    growth={platformOverview.revenueGrowth}
                    growthSuffix={t('analytics.admin.vsPreviousPeriod')}
                  />
                  <KpiCard
                    icon="📦" label={t('analytics.totalOrders')} accentColor="#10b981"
                    value={formatInteger(platformOverview.totalOrders)}
                    growth={platformOverview.orderGrowth}
                    growthSuffix={t('analytics.admin.vsPreviousPeriod')}
                    sub={`✓ ${platformOverview.completedOrders} ${t('analytics.completed')}  ·  ✗ ${platformOverview.cancelledOrders} ${t('analytics.cancelled')}`}
                  />
                  <KpiCard
                    icon="🧾" label={t('analytics.avgOrderValue')} accentColor="#f59e0b"
                    value={fmtMoney(platformOverview.avgOrderValue)}
                    sub={`${platformOverview.uniqueTables} ${t('analytics.uniqueTablesServed')}`}
                  />
                  <KpiCard
                    icon="🏪" label={t('analytics.admin.activeRestaurants')} accentColor="#8b5cf6"
                    value={platformOverview.activeRestaurants}
                    sub={t('analytics.admin.withOrdersThisPeriod')}
                  />
                  <KpiCard
                    icon="✅" label={t('analytics.admin.completionRate')} accentColor="#06b6d4"
                    value={
                      platformOverview.totalOrders > 0
                        ? `${((platformOverview.completedOrders / platformOverview.totalOrders) * 100).toFixed(1)}%`
                        : '—'
                    }
                    sub={`${platformOverview.cancelledOrders} ${t('analytics.cancelled')}`}
                  />
                </>
              )
            }
          </div>

          {/* Platform revenue trend — full width */}
          <div className="aad-card aad-card-full">
            <div className="aad-card-head">
              <h3>📈 {t('analytics.admin.platformRevenueTrend')}</h3>
              <span className="aad-card-note">{t('analytics.admin.allRestaurantsCombined')}</span>
            </div>
            <div className="aad-chart-body">
              {platformLoading
                ? <Skel h={260} />
                : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={platformTrend} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13 }}
                        formatter={(v, name) => [
                          name === t('analytics.revenue') ? fmtMoney(v) : v,
                          name
                        ]}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="revenue"     stroke="#ea580c" strokeWidth={3} dot={{ r: 4 }} name={t('analytics.revenue')} />
                      <Line type="monotone" dataKey="order_count" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name={t('analytics.chartOrders')} />
                    </LineChart>
                  </ResponsiveContainer>
                )
              }
            </div>
          </div>

          {/* Revenue by restaurant — horizontal bar */}
          <div className="aad-card aad-card-full">
            <div className="aad-card-head">
              <h3>🏪 Revenue by Restaurant</h3>
              <span className="aad-card-note">Side-by-side comparison</span>
            </div>
            <div className="aad-chart-body">
              {platformLoading
                ? <Skel h={Math.max(200, perRestaurant.length * 44)} />
                : (
                  <ResponsiveContainer width="100%" height={Math.max(200, perRestaurant.length * 48)}>
                    <BarChart
                      data={perRestaurant}
                      layout="vertical"
                      margin={{ top: 4, right: 24, left: 4, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                      <YAxis type="category" dataKey="restaurant_name" tick={{ fill: '#374151', fontSize: 13 }} width={140} />
                      <Tooltip
                        contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}
                        formatter={(v) => [fmtMoney(v), t('analytics.revenue')]}
                      />
                      <Bar dataKey="total_revenue" radius={[0, 8, 8, 0]} name={t('analytics.revenue')}>
                        {perRestaurant.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )
              }
            </div>
          </div>

          {/* Leaderboard table */}
          <div className="aad-card aad-card-full">
            <div className="aad-card-head">
              <h3>🏆 {t('analytics.admin.restaurantLeaderboard')}</h3>
              <span className="aad-card-note">{t('analytics.admin.clickRowForAnalytics')}</span>
            </div>
            <div className="aad-table-wrap">
              {platformLoading
                ? (
                  <div className="aad-table-skels">
                    {[...Array(5)].map((_, i) => <Skel key={i} h={48} />)}
                  </div>
                )
                : (
                  <table className="aad-table">
                    <thead>
                      <tr>
                        <th style={{ width: 48 }}>#</th>
                        <th>Restaurant</th>
                        <th className="r">Revenue</th>
                        <th className="r">Orders</th>
                        <th className="r">Completed</th>
                        <th className="r">Cancelled</th>
                        <th className="r">Avg Order</th>
                        <th className="r aad-hide-sm">Avg Prep</th>
                        <th className="r aad-hide-sm">Tables</th>
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {perRestaurant.map((row, i) => (
                        <tr key={row.restaurant_id} className="aad-row-clickable" onClick={() => drillInto(row)}>
                          <td className="aad-rank-cell">
                            {i === 0 && '🥇'}
                            {i === 1 && '🥈'}
                            {i === 2 && '🥉'}
                            {i  > 2 && <span className="aad-rank-num">{i + 1}</span>}
                          </td>
                          <td>
                            <span className="aad-rest-dot" style={{ background: COLORS[i % COLORS.length] }} />
                            <span className="aad-rest-name">{row.restaurant_name}</span>
                          </td>
                          <td className="r aad-money">{fmtMoney(row.total_revenue)}</td>
                          <td className="r">{row.total_orders}</td>
                          <td className="r aad-green">{row.completed_orders}</td>
                          <td className="r aad-red">{row.cancelled_orders}</td>
                          <td className="r">{fmtMoney(row.avg_order_value)}</td>
                          <td className="r aad-hide-sm">{row.avg_prep_time > 0 ? `${row.avg_prep_time} min` : '—'}</td>
                          <td className="r aad-hide-sm">{row.unique_tables}</td>
                          <td><span className="aad-view-chip">{t('analytics.admin.view')}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          SINGLE-RESTAURANT DRILL-DOWN VIEW
          Mirrors the existing manager AnalyticsDashboard exactly, but inline.
          ════════════════════════════════════════════════════════════════════════ */}
      {view === 'single' && (
        <>
          {/* KPI Cards */}
          <div className="aad-kpi-grid">
            {singleLoading
              ? [...Array(4)].map((_, i) => <Skel key={i} h={116} />)
              : singleOverview && (
                <>
                  <KpiCard
                    icon="💰" label={t('analytics.totalRevenue')} accentColor="#ea580c"
                    value={<SinglePrice amount={singleOverview.totalRevenue} size="lg" />}
                    growth={singleOverview.revenueGrowth}
                    growthSuffix={t('analytics.admin.vsPreviousPeriod')}
                  />
                  <KpiCard
                    icon="📦" label={t('analytics.totalOrders')} accentColor="#10b981"
                    value={singleOverview.totalOrders}
                    growth={singleOverview.orderGrowth}
                    growthSuffix={t('analytics.admin.vsPreviousPeriod')}
                    sub={`✓ ${singleOverview.completedOrders} ${t('analytics.completed')}  ·  ✗ ${singleOverview.cancelledOrders} ${t('analytics.cancelled')}`}
                  />
                  <KpiCard
                    icon="🧾" label={t('analytics.avgOrderValue')} accentColor="#f59e0b"
                    value={<SinglePrice amount={singleOverview.avgOrderValue} size="lg" />}
                    sub={`${singleOverview.uniqueTables} ${t('analytics.tablesServed')}`}
                  />
                  <KpiCard
                    icon="⏱️" label={t('analytics.avgPrepTime')} accentColor="#8b5cf6"
                    value={`${singleOverview.avgPrepTime} ${t('analytics.min')}`}
                    sub={t('analytics.rangeMin', { min: singleOverview.minPrepTime, max: singleOverview.maxPrepTime })}
                  />
                </>
              )
            }
          </div>

          {/* Sales Trend + Peak Hours — side by side */}
          <div className="aad-two-col">
            {/* Sales Trend */}
            <div className="aad-card">
              <div className="aad-card-head"><h3>📈 {t('analytics.salesTrend')}</h3></div>
              <div className="aad-chart-body">
                {singleLoading
                  ? <Skel h={240} />
                  : (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={singleTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                        <Tooltip
                          contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}
                          formatter={(v, name) => [
                            name === t('analytics.revenue') ? $s(v) : v,
                            name
                          ]}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="revenue"     stroke="#ea580c" strokeWidth={3} dot={{ r: 4 }} name={t('analytics.revenue')} />
                        <Line type="monotone" dataKey="order_count" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name={t('analytics.chartOrders')} />
                      </LineChart>
                    </ResponsiveContainer>
                  )
                }
              </div>
            </div>

            {/* Peak Hours */}
            <div className="aad-card">
              <div className="aad-card-head"><h3>🕐 Peak Hours</h3></div>
              <div className="aad-chart-body">
                {singleLoading
                  ? <Skel h={240} />
                  : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={singlePeakHours} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="hour" tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={(h) => `${h}h`} />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}
                          labelFormatter={(h) => `${h}:00 – ${h + 1}:00`}
                        />
                        <Bar dataKey="orderCount" fill="#ea580c" radius={[6, 6, 0, 0]} name={t('analytics.chartOrders')} />
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </div>
            </div>
          </div>

          {/* Revenue by Category */}
          {(singleLoading || singleCategories.length > 0) && (
            <div className="aad-card aad-card-full">
              <div className="aad-card-head"><h3>🗂️ Revenue by Category</h3></div>
              <div className="aad-chart-body">
                {singleLoading
                  ? <Skel h={220} />
                  : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={singleCategories}
                        layout="vertical"
                        margin={{ top: 4, right: 24, left: 4, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                        <YAxis type="category" dataKey="category" tick={{ fill: '#374151', fontSize: 12 }} width={110} />
                        <Tooltip
                          contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}
                          formatter={(v) => [$s(v), t('analytics.revenue')]}
                        />
                        <Bar dataKey="total_revenue" radius={[0, 8, 8, 0]} name={t('analytics.revenue')}>
                          {singleCategories.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </div>
            </div>
          )}

          {/* Top Items table */}
          {(singleLoading || singleTopItems.length > 0) && (
            <div className="aad-card aad-card-full">
              <div className="aad-card-head"><h3>🏆 Top Selling Items</h3></div>
              <div className="aad-table-wrap">
                {singleLoading
                  ? <div className="aad-table-skels">{[...Array(5)].map((_, i) => <Skel key={i} h={48} />)}</div>
                  : (
                    <table className="aad-table">
                      <thead>
                        <tr>
                          <th style={{ width: 48 }}>#</th>
                          <th>{t('analytics.item')}</th>
                          <th>{t('analytics.category')}</th>
                          <th className="r">{t('analytics.admin.timesOrdered')}</th>
                          <th className="r">{t('analytics.admin.qtySold')}</th>
                          <th className="r">{t('analytics.revenue')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {singleTopItems.map((item, i) => (
                          <tr key={item.id}>
                            <td className="aad-rank-cell">
                              {i === 0 && '🥇'}
                              {i === 1 && '🥈'}
                              {i === 2 && '🥉'}
                              {i  > 2 && <span className="aad-rank-num">{i + 1}</span>}
                            </td>
                            <td>
                              <div className="aad-item-cell">
                                {item.image && <img src={item.image} alt={item.name} className="aad-item-thumb" />}
                                <span className="aad-item-name">{item.name}</span>
                              </div>
                            </td>
                            <td>{item.category || '—'}</td>
                            <td className="r">{item.times_ordered}</td>
                            <td className="r">{item.total_quantity}</td>
                            <td className="r aad-money"><SinglePrice amount={item.total_revenue} size="sm" /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            </div>
          )}

          {/* Order Status Summary */}
          {(singleLoading || singleStatus) && (
            <div className="aad-card aad-card-full">
              <div className="aad-card-head"><h3>📊 {t('analytics.admin.orderStatusSummary')}</h3></div>
              <div className="aad-card-body">
                {singleLoading
                  ? <Skel h={100} />
                  : (
                    <>
                      <div className="aad-status-grid">
                        {(singleStatus?.statusBreakdown || []).map((s) => (
                          <div key={s.status} className={`aad-status-tile aad-status-${s.status}`}>
                            <div className="aad-status-count">{s.count}</div>
                            <div className="aad-status-name">{s.status}</div>
                            <div className="aad-status-val"><SinglePrice amount={s.total_value} size="sm" /></div>
                          </div>
                        ))}
                      </div>

                      {singleStatus?.cancellationReasons?.length > 0 && (
                        <div className="aad-cancel-section">
                          <h4 className="aad-cancel-title">{t('analytics.admin.topCancellationReasons')}</h4>
                          {singleStatus.cancellationReasons.map((r, i) => (
                            <div key={i} className="aad-cancel-row">
                              <span>{r.cancellation_reason || t('analytics.noReasonProvided')}</span>
                              <span className="aad-cancel-badge">{r.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )
                }
              </div>
            </div>
          )}
        </>
      )}
    </div>
    </AppShell>
  );
};

export default AdminAnalyticsDashboard;

